import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const SESSION_STORAGE_KEY = 'stpk_session_started_at';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStartedAt, setSessionStartedAt] = useState(() => {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(SESSION_STORAGE_KEY) : null;
    return raw ? parseInt(raw, 10) : null;
  });

  const stampSession = useCallback((ts) => {
    const value = ts || Date.now();
    setSessionStartedAt(value);
    try { localStorage.setItem(SESSION_STORAGE_KEY, String(value)); } catch (_) { /* noop */ }
  }, []);

  const clearSessionStamp = useCallback(() => {
    setSessionStartedAt(null);
    try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch (_) { /* noop */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      // If we have a valid session but no stamp yet (e.g. after a refresh),
      // anchor it to now so the expiry warning has something to count from.
      if (!localStorage.getItem(SESSION_STORAGE_KEY)) stampSession();
    } catch (e) {
      setUser(false);
      clearSessionStamp();
    } finally {
      setLoading(false);
    }
  }, [stampSession, clearSessionStamp]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    stampSession();
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(data);
    stampSession();
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
      clearSessionStamp();
    }
  };

  const refreshSession = useCallback(() => stampSession(), [stampSession]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, sessionStartedAt, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
